// Pure Print Center selection rules. No React, no Redux: every rule about
// what is selected, how many labels each line prints and what blocks a job is
// here and unit-tested.
//
// A line prints `newStock + extra` labels:
//   newStock — settles received stock still waiting for labels (≤ outstanding)
//   extra    — manual labels, reprints, damaged-label replacements; never
//              touches label demand.

export const SELECTION_ORIGINS = Object.freeze({
  NEEDS_LABELS: "needs_labels",
  CATALOG: "catalog",
  SCAN: "scan",
  REPRINT: "reprint",
});

export const LARGE_JOB_LABELS = 500;

export const isSelectableItem = (item) => Boolean(item) && !item.blockedReason;

export const lineQuantity = (entry) =>
  Math.max(0, Number(entry?.newStock || 0)) + Math.max(0, Number(entry?.extra || 0));

/**
 * Workflow-aware defaults:
 *   Needs labels → the outstanding labels, as new stock
 *   Catalog/scan → 1 extra label
 *   Reprint      → the original quantity, as extras
 */
export const defaultQuantitiesFor = (item, origin, { quantity } = {}) => {
  const outstanding = Math.max(0, Number(item?.demand?.outstanding || 0));
  if (origin === SELECTION_ORIGINS.NEEDS_LABELS) {
    const newStock = quantity != null ? Math.min(outstanding, Math.max(0, quantity)) : outstanding;
    return { newStock, extra: 0 };
  }
  if (origin === SELECTION_ORIGINS.REPRINT) {
    return { newStock: 0, extra: Math.max(1, Number(quantity || 1)) };
  }
  return { newStock: 0, extra: Math.max(0, Number(quantity ?? 1)) };
};

/** "none" | "some" | "all" for a group of items (product, colour, size column). */
export const getGroupSelectionState = (items, entries) => {
  const selectable = (items || []).filter(isSelectableItem);
  if (!selectable.length) return "none";
  const selected = selectable.filter((item) => Boolean(entries?.[item.key])).length;
  if (!selected) return "none";
  return selected === selectable.length ? "all" : "some";
};

export const summarizeSelection = (entries, order) => {
  const list = (order || Object.keys(entries || {})).map((key) => entries[key]).filter(Boolean);
  const printing = list.filter((entry) => lineQuantity(entry) > 0);
  return {
    lines: list.length,
    printingLines: printing.length,
    products: new Set(printing.map((entry) => entry.item.productId)).size,
    labels: printing.reduce((sum, entry) => sum + lineQuantity(entry), 0),
    newStock: printing.reduce((sum, entry) => sum + Number(entry.newStock || 0), 0),
    extra: printing.reduce((sum, entry) => sum + Number(entry.extra || 0), 0),
    zeroLines: list.length - printing.length,
  };
};

/**
 * What stops a job (blocking) and what only deserves attention (warnings).
 * Zero-quantity lines are not an error: they are listed and left out.
 */
export const validateSelection = (entries, order) => {
  const list = (order || Object.keys(entries || {})).map((key) => entries[key]).filter(Boolean);
  const blocking = [];
  const warnings = [];

  list.forEach((entry) => {
    const { item } = entry;
    const quantity = lineQuantity(entry);
    if (entry.unavailable) {
      blocking.push({ code: "unavailable", key: item.key, item });
      return;
    }
    if (item.blockedReason) {
      blocking.push({ code: item.blockedReason, key: item.key, item });
      return;
    }
    if (quantity <= 0) return;
    const outstanding = Math.max(0, Number(item.demand?.outstanding || 0));
    if (Number(entry.newStock || 0) > outstanding) {
      blocking.push({
        code: "new_stock_exceeds_outstanding",
        key: item.key,
        item,
        requested: Number(entry.newStock || 0),
        available: outstanding,
      });
    }
    if (item.productStatus === "archived") {
      warnings.push({ code: "archived_product", key: item.key, item });
    }
    if (item.onHand != null && quantity > Number(item.onHand)) {
      warnings.push({ code: "quantity_exceeds_on_hand", key: item.key, item, quantity, onHand: Number(item.onHand) });
    }
  });

  const { labels, zeroLines } = summarizeSelection(entries, order);
  if (labels > LARGE_JOB_LABELS) warnings.push({ code: "large_job", labels });
  if (zeroLines > 0) warnings.push({ code: "zero_lines", count: zeroLines });

  return { blocking, warnings };
};

/** The job source is context only; accounting is per line. */
export const resolveJobSource = (entries, order) => {
  const list = (order || Object.keys(entries || {})).map((key) => entries[key]).filter(Boolean);
  if (list.some((entry) => Number(entry.newStock || 0) > 0)) return SELECTION_ORIGINS.NEEDS_LABELS;
  if (list.length && list.every((entry) => entry.origin === SELECTION_ORIGINS.REPRINT)) return SELECTION_ORIGINS.REPRINT;
  if (list.length && list.every((entry) => entry.origin === SELECTION_ORIGINS.SCAN)) return SELECTION_ORIGINS.SCAN;
  return SELECTION_ORIGINS.CATALOG;
};

export const resolveReprintOfJobId = (entries, order) => {
  const ids = new Set(
    (order || Object.keys(entries || {}))
      .map((key) => entries[key]?.reprintOfJobId)
      .filter(Boolean)
  );
  return ids.size === 1 ? [...ids][0] : null;
};

/** API lines for a job, in tray order, without zero-quantity lines. */
export const buildJobLines = (entries, order) =>
  (order || Object.keys(entries || {}))
    .map((key) => entries[key])
    .filter((entry) => entry && lineQuantity(entry) > 0)
    .map((entry) => ({
      key: entry.item.key,
      quantity: lineQuantity(entry),
      newStockQuantity: Math.max(0, Number(entry.newStock || 0)),
    }));

/** Ordered tray lines grouped by product for display. */
export const groupEntriesByProduct = (entries, order) => {
  const groups = new Map();
  (order || Object.keys(entries || {})).forEach((key) => {
    const entry = entries[key];
    if (!entry) return;
    const productId = entry.item.productId;
    const group = groups.get(productId) || {
      productId,
      productTitle: entry.item.productTitle,
      baseSku: entry.item.baseSku,
      brandName: entry.item.brandName,
      imageUrl: entry.item.imageUrl,
      entries: [],
    };
    group.entries.push(entry);
    groups.set(productId, group);
  });
  return [...groups.values()];
};

/** Quantity actions over the whole selection. */
export const applyBulkQuantity = (entry, action) => {
  const outstanding = Math.max(0, Number(entry.item?.demand?.outstanding || 0));
  switch (action) {
    case "all_one":
      return { newStock: 0, extra: 1 };
    case "on_hand":
      return {
        newStock: Math.min(Number(entry.newStock || 0), Math.max(0, Number(entry.item?.onHand || 0))),
        extra: Math.max(0, Number(entry.item?.onHand || 0) - Math.min(Number(entry.newStock || 0), Number(entry.item?.onHand || 0))),
      };
    case "outstanding":
      return { newStock: outstanding, extra: 0 };
    default:
      return { newStock: Number(entry.newStock || 0), extra: Number(entry.extra || 0) };
  }
};
